import { CommonPlayerLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/player/logic/common-player-logic';
import { IReactionDisposer, reaction } from 'mobx';
import type { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import type { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { RenderPostionType } from '@src/js/constant';
import { checkHasAnimation } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { getTargetComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-component-relate';
import {
    isAudioElement,
    isDomGifPictureModel,
    isMediaElement,
    isVideoElement,
} from '@src/slide/domain/ui-model/elements/element-judge';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IMediaManager, MediaRegion } from '@src/slide/application/service/media-manager/media-manager.interface';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IMobileTouchService } from '@src/slide/application/service/mobile-touch-service/mobile-touch-service.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';

export class PicturePlayerLogic extends CommonPlayerLogic {
    public selectedListener: IReactionDisposer;
    public showVideoDomListener: IReactionDisposer;
    public dragListener: IReactionDisposer;
    public mediaManager: IMediaManager | undefined;
    public playerRenderGifPosterListener: IReactionDisposer;
    declare protected readonly factoryOptions: InterfaceFactoryOptions<IPictureModel>;

    public constructor(factoryOptions: InterfaceFactoryOptions<IPictureModel>, component: ComponentFactory) {
        super(factoryOptions, component);
        const { model } = this.factoryOptions;
        this.mediaManager = this.factoryOptions.mediaManager;
        // 事件绑定
        this.component.inner.onMouseEnter(this.handleInnerMouseEnter);
        this.component.inner.onClick(this.handleInnerClick);
        this.component.inner.onTouchStart(this.handleInnerClick);
        if (browser.isPad) {
            this.component.inner.onMouseDown(this.handleMouseDown);
        }

        if (isDomGifPictureModel(model)) {
            this.addPlayerRenderGifPosterListener();
        }
        if (isVideoElement(model)) {
            this.addShowVideoDomListener();
        }
    }

    public dispose() {
        super.dispose();
        this.playerRenderGifPosterListener?.();
        this.showVideoDomListener?.();
    }

    private handleMouseDown = (event) => {
        // ipad 音视频元素阻止 mousedown 冒泡，避免演示态触发切页
        if (isMediaElement(this.factoryOptions.model)) {
            event.nativeEvent.stopPropagation();
        }
    };

    private isMediaSwitchAvailable() {
        const { model } = this.factoryOptions;

        const hasAnimation = checkHasAnimation(
            model.rootGroupModel?.id ?? model.id ?? '',
            this.getSelectionService()?.currentSlide,
        );

        const animWrapper = getTargetComponent(
            model.rootGroupModel?.key ?? model.key,
            this.getAppServices().viewableServiceLayer?.animManager?.playSlideComponent,
        );

        // 当音视频没有动画，或者音视频元素有显示的情况下，可以从图片切换成 dom
        return !hasAnimation || animWrapper.alpha !== 0;
    }

    private handleInnerMouseEnter = () => {
        if (isAudioElement(this.factoryOptions.model)) {
            if (this.isMediaSwitchAvailable()) {
                // 显示出附件 dom 元素，从而才能显示出附件的浮动播放条
                this.mediaManager?.updateElementKeyByRegion(this.factoryOptions.model.key, MediaRegion.PLAYER);
            }
        }

        // 动画入场之前 hover不显示小手，入场后需要更新能显示小手
        if (isMediaElement(this.factoryOptions.model)) {
            this.component.inner.playerIconComponent.updateComponent();
        }
    };

    private handleInnerClick = () => {
        if (isVideoElement(this.factoryOptions.model)) {
            if (this.isMediaSwitchAvailable()) {
                if (browser.isMobile && this.getMobileTouchService()) {
                    this.getMobileTouchService().isTouchOnMediaElement = true;
                }
                this.mediaManager?.playVideoByRegion(this.factoryOptions.model.key, MediaRegion.PLAYER);
            }
        }
        if (isAudioElement(this.factoryOptions.model)) {
            if (this.isMediaSwitchAvailable()) {
                if (browser.isMobile && this.getMobileTouchService()) {
                    this.getMobileTouchService().isTouchOnMediaElement = true;
                }
                this.mediaManager?.playAudioByRegion(this.factoryOptions.model.key, MediaRegion.PLAYER);
            }
        }
    };

    private addPlayerRenderGifPosterListener = () => {
        const { model } = this.factoryOptions;
        this.playerRenderGifPosterListener = reaction(
            () => this.factoryOptions.mediaManager?.shouldPlayerRenderGifPoster(model.key),
            () => {
                if (this.factoryOptions.region === RenderPostionType.PLAYER) {
                    this.component.updateComponent();
                }
            },
        );
    };

    private addShowVideoDomListener = () => {
        const { model, mediaManager } = this.factoryOptions;
        this.showVideoDomListener = reaction(() => mediaManager?.shouldShowMediaElement(
            model.key,
            model.id as string,
            MediaRegion.PLAYER,
        ), () => {
            this.component.updateComponent();
        });
    };

    private getAppServices(): ISlideBaseAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            ISlideBaseAppServices;
    }

    private getSelectionService(): ISelectionService | undefined {
        const appService = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appService?.viewableServiceLayer?.selectionService;
    }

    private getMobileTouchService(): IMobileTouchService {
        return (this.getAppServices() as IMobileEditorAppServices).viewableServiceLayer.mobileTouchService;
    }
}
